import { Grid } from '@mui/material';
import { useCallback, useMemo } from 'react';

import { getOptions } from '@shared/commonFunctions';
import { SelectAutocomplete } from '@shared/inputs/SelectAutocomplete';
import { EStipTypeEntity, IOption, IStipType } from '@shared/interfaces';
import { AddButton } from '@styles/ContactManager/HeaderStyles';
import { Notistack } from '@shared/components/Notistack/Notistack';
import { IAssetOption } from '../StipRequestModal';

interface HeaderProps {
  selectedStipType: IStipType | null;
  selectedContact: string | null;
  selectedAsset: IAssetOption | null;
  contactsOptions: IOption<unknown>[];
  stipTypeOptions: IOption<unknown>[];
  assetOptions: { id: string; display: string }[];
  selectStipType: (stipTypeId: string) => void;
  selectContact: (value: string) => void;
  handleAddStip: () => void;
  selectAsset: (value: IAssetOption) => void;
  handleValidateAssetSelect?: (assetId: string) => void;
}

export const Header = ({
  selectedStipType,
  stipTypeOptions,
  selectStipType,
  selectedContact,
  contactsOptions,
  assetOptions,
  selectedAsset,
  selectContact,
  selectAsset,
  handleAddStip,
  handleValidateAssetSelect,
}: HeaderProps) => {
  const disableAddButton = useMemo(
    () =>
      (selectedStipType?.relatedToEntity === EStipTypeEntity.CONTACT && (!selectedStipType || !selectedContact)) ||
      (selectedStipType?.relatedToEntity === EStipTypeEntity.ASSET && (!selectedStipType || !selectedAsset?.id)) ||
      ((selectedStipType?.relatedToEntity !== EStipTypeEntity.CONTACT ||
        selectedStipType?.relatedToEntity !== EStipTypeEntity.CONTACT) &&
        !selectedStipType),
    [selectedStipType?.relatedToEntity, selectedStipType, selectedContact, selectedAsset?.id, selectedAsset]
  );

  const handleChange = useCallback(
    (id: string) => {
      const option = (getOptions(assetOptions, 'id', 'display') || []).find((opt) => opt.value === id);
      selectAsset({ id: option?.value || '', display: option?.label || '' } as IAssetOption);
      handleValidateAssetSelect && handleValidateAssetSelect(id ?? '');
    },
    [selectAsset, assetOptions]
  );

  if (selectedStipType?.relatedToEntity === EStipTypeEntity.ASSET && !selectedAsset?.id) {
    Notistack.generalError(
      'Please select an Asset in order to add an Asset Stip, or remove Stip type to add on the left dropdown'
    );
  }

  const filteredStipTypeOptions = useMemo(() => {
    return stipTypeOptions?.filter(
      (stip) => !stip.label.toLowerCase().startsWith('emergency contacts') // Case-insensitive filtering
    );
  }, [stipTypeOptions]);

  return (
    <>
      <Grid
        container
        alignItems="center"
        spacing={2}
      >
        <Grid
          item
          xs={3}
        >
          <SelectAutocomplete
            label="Select Stip Type to Add"
            value={selectedStipType?.id}
            options={filteredStipTypeOptions}
            onChange={selectStipType}
          />
        </Grid>

        {selectedStipType?.relatedToEntity === EStipTypeEntity.CONTACT && (
          <Grid
            item
            xs={3}
          >
            <SelectAutocomplete
              label={`Select ${selectedStipType?.relatedToEntity ?? ''}`}
              value={selectedContact}
              options={contactsOptions}
              onChange={selectContact}
            />
          </Grid>
        )}

        {selectedStipType?.relatedToEntity === EStipTypeEntity.ASSET && (
          <Grid
            item
            xs={3}
          >
            <SelectAutocomplete
              label={`Select ${selectedStipType?.relatedToEntity ?? ''}`}
              value={selectedAsset?.id}
              options={getOptions(assetOptions, 'id', 'display')}
              onChange={handleChange}
            />
          </Grid>
        )}

        <AddButton
          size="large"
          variant="text"
          disabled={disableAddButton}
          onClick={handleAddStip}
        >
          Add
        </AddButton>
      </Grid>
    </>
  );
};
